import React from 'react';
import type { ProductRow } from '../../api/assets';
import { thumbUrl } from '../../utils/imgUrl';

// 商品采集中心词表与小工具: 采集目标类目 / 字段中文名 / 稀疏字段格式化.

// 采集目标 key → 中文 (categories.py PLAN 里的 target; 未登记的原样显示).
//
// ⚠️ **这张表只管显示, 不要再用它当"有哪些类目"的来源。** 2026-10-08 踩过:
// 审核页的采集类目下拉是 `Object.entries(TARGET_LABEL)` 渲染的, 采集侧铺到 61 个类目时
// 它还停在 35 个(只有 21 个对得上), 于是 40 个类目在筛选里根本不存在 —— hat 有 19,853 个
// 商品, 审核员却筛不到。要列类目一律走 `/v1/assets/products/options`(后端 GROUP BY target
// 实时算)。这里登记不全时, targetText 的 `|| t` 会退回显示英文 key, 代价只是不好看。
export const TARGET_LABEL: Record<string, string> = {
    earphone: '耳机', phone: '手机', computer: '电脑', frame: '相框', magnet: '磁贴', necklace: '项链', usb_drive: 'U 盘',
    award: '奖杯奖牌', backpack: '背包', belt: '皮带', bracelet: '手链', card_holder: '卡包', coaster: '杯垫', tumbler: '保温杯',
    wallet: '钱包', keychain: '钥匙扣', mug: '马克杯', cutting_board: '砧板', notebook: '笔记本', pen: '笔', lighter: '打火机',
    watch: '手表', sunglasses: '太阳镜', ring: '戒指', earring: '耳环', pendant: '吊坠', dog_tag: '狗牌', ornament: '挂饰',
    sign: '标牌', plaque: '牌匾', flask: '酒壶', bottle: '水瓶', case: '手机壳', headphone: '头戴耳机', tablet: '平板',
    // 2026-10 采集侧新铺的 40 个类目 (生产 product 表实际在用, 之前一个都没登记).
    hat: '帽子', hdd: '移动硬盘', knife: '刀具', lipstick: '口红', luggage_tag: '行李牌', mirror: '镜子',
    name_badge: '工牌', name_plate: '铭牌', party_favor: '派对小礼品', pencil: '铅笔', pencil_case: '笔袋',
    perfume: '香水', pet_collar: '宠物项圈', pet_tag: '宠物牌', phone_case: '手机壳', stamp: '印章',
    water_bottle: '水壶', building_block: '积木', bookmark: '书签', collect_card: '收藏卡', crystal: '水晶摆件',
    fountain_pen: '钢笔', greeting_card: '贺卡', hair_bow: '发饰蝴蝶结', invitation: '请柬', metal_pen: '金属笔',
    puzzle_3d: '3D 立体拼图', jigsaw: '拼图', planner: '日程本', desk_organizer: '桌面收纳', phone_stand: '手机支架',
    power_bank: '移动电源', balsa_glider: '轻木滑翔机', spinning_top: '陀螺', brain_teaser: '益智玩具',
    certificate: '证书', sign_plaque: '标牌牌匾', tool_leather: '皮雕工具', money_bank: '存钱罐', speaker: '音箱',
};
export const targetText = (t: string | null | undefined) => (t ? TARGET_LABEL[t] || t : '—');

// 类目归属判定 (Keepa 类目可能判错, 采集器复筛): tree 在目标树内 / root 仅根类目一致 / miss 归错.
export const SCOPE_LABEL: Record<string, { text: string; cls: string }> = {
    tree: { text: '类目内', cls: 'bg-emerald-50 text-emerald-600' },
    root: { text: '仅根类目', cls: 'bg-amber-50 text-amber-600' },
    miss: { text: '归错类', cls: 'bg-rose-50 text-rose-500' },
};
export function ScopeBadge({ scope }: { scope: string | null | undefined }) {
    const s = scope ? SCOPE_LABEL[scope] : undefined;
    if (!s) return <span className="text-slate-300">—</span>;
    return <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${s.cls}`}>{s.text}</span>;
}

// product 表全部业务字段的中文名 (与飞书「Keepa 采集字段抽取映射」一致); 详情 key-value 用.
export const FIELD_LABEL: Record<string, string> = {
    asin: 'ASIN', title: '商品名称', brand: '品牌', model: '型号', brandStoreName: '品牌店名称', brandStoreUrl: '品牌店链接',
    rootCategory: '根类目', categoryTree: '类目树', categoryPath: '类目路径', manufacturer: '制造商', partNumber: '零件号', binding: '绑定/类型',
    materials: '材质', materialsJson: '材质原始数组', color: '颜色', features: '特性要点', ingredients: '成分', itemForm: '形态', pattern: '图案/纹路',
    description: '商品描述', style: '风格/版本', includedComponents: '含哪些组件', size: '尺寸规格',
    itemHeightMm: '实物高 (mm)', itemLengthMm: '实物长 (mm)', itemWidthMm: '实物宽 (mm)', itemWeightG: '实物重量 (g)', thicknessMm: '厚度 (mm, 推断)',
    numberOfItems: '套装数量', unitCount: '单位数量', packageHeightMm: '包装高 (mm)', packageLengthMm: '包装长 (mm)', packageWidthMm: '包装宽 (mm)', packageWeightG: '包装重 (g)',
    isHeatSensitive: '热敏感', hazardousMaterials: '危险品信息', batteriesRequired: '需要电池', batteriesIncluded: '含电池', safetyWarning: '安全警告',
    imageCount: '图片数量', variations: '颜色/尺寸变体', monthlySold: '月销量', salesRankRef: '销量排名参考类目', salesRanks: '销量排名历史',
    listedSince: '上市时间', releaseDate: '发售日期', eanList: 'EAN', upcList: 'UPC', gtinList: 'GTIN',
    target: '采集目标类目', sourceNodeId: '来源榜单节点', bestSellerRank: '榜单位次', inScope: '类目归属判定', fetchStatus: '拉取状态',
    discoveredAt: '发现时间', fetchedAt: '拉取详情时间',
};

// 详情 key-value 里跳过的字段 (已在别处专门展示, 或纯内部).
export const FIELD_SKIP = new Set(['asin', 'title', 'materialsJson', 'features', 'variations', 'description', 'images', 'fetchStatus', 'categoryTree', 'salesRanks']);

export function parseJSON<T>(s: string | null | undefined, fallback: T): T {
    if (!s) return fallback;
    try { return JSON.parse(s) as T; } catch { return fallback; }
}

// 材质数组: 优先 materials_json, 退回按 " | " 切 materials.
export function materialList(p: Pick<ProductRow, 'materials' | 'materialsJson'>): string[] {
    const arr = parseJSON<unknown>(p.materialsJson, null);
    if (Array.isArray(arr)) return arr.filter((x): x is string => typeof x === 'string' && x !== '');
    return (p.materials || '').split('|').map((s) => s.trim()).filter(Boolean);
}

export function MaterialChips({ items, max = 3, size = 'sm', nowrap = false }: { items: string[]; max?: number; size?: 'sm' | 'md'; nowrap?: boolean }) {
    if (items.length === 0) return <span className="text-slate-300">—</span>;
    const shown = items.slice(0, max);
    const rest = items.length - shown.length;
    const cls = size === 'md' ? 'px-2 py-0.5 text-xs' : 'px-1.5 py-px text-[11px]';
    // nowrap: 表格里单行, 每片最多 ~14 字符截断, 悬停看全文 (亚马逊材质常是整句自由文本).
    const chip = nowrap ? 'inline-block max-w-[100px] truncate align-bottom whitespace-nowrap' : '';
    return (
        <span className={`inline-flex gap-1 align-middle ${nowrap ? 'flex-nowrap' : 'flex-wrap'}`}>
            {shown.map((m) => <span key={m} className={`rounded bg-teal-50 text-teal-700 ${cls} ${chip}`} title={m}>{m}</span>)}
            {rest > 0 && <span className={`rounded bg-slate-100 text-slate-500 ${cls}`} title={items.slice(max).join(' | ')}>+{rest}</span>}
        </span>
    );
}

// 图片占位: 采集器先回填 URL 再异步上传, 存储上可能还没有 (早期采集的少数文件上传失败未重试, 排行榜头部有残留 404),
// 加载失败显示占位而不是破图.
function BrokenImg({ className, style, onClick }: {
    className?: string; style?: React.CSSProperties; onClick?: (e: React.MouseEvent<HTMLElement>) => void;
}) {
    return (
        <div className={`flex items-center justify-center bg-slate-50 text-slate-300 ${className || ''}`} style={style} title="图片尚未上传到本平台存储" onClick={onClick}>
            <svg width="40%" height="40%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 16 5-5 4 4 3-3 6 6" /><circle cx="16" cy="9" r="1.5" /></svg>
        </div>
    );
}

/**
 * 单张图 (缩略图 / 详情大图): 哪张没上传就该张显示占位, 不跳图 —— 用户点的就是这一张.
 *
 * **默认拿图床缩略图** (见 utils/imgUrl): 列表/图墙直出原图会把页面滑死 (实测一页 35 张
 * 原图 7.9MB、解码上百 MB 位图)。要原图的场景 (详情里的大图) 传 thumb={false};
 * 放大查看走 ImageLightbox, 那边本来就用原图。
 * decoding="async" 让解码不占主线程 —— 这是"滚动卡顿"与"图片晚一点出来"的取舍, 选后者。
 */
export function ProductImg({ src, className, style, onClick, title, thumb = true, thumbW }: {
    src: string; className?: string; style?: React.CSSProperties;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void; title?: string;
    thumb?: boolean; thumbW?: number;
}) {
    const [broken, setBroken] = React.useState(false);
    React.useEffect(() => { setBroken(false); }, [src]);
    if (broken) {
        return <BrokenImg className={className} style={style} onClick={onClick} />;
    }
    const shown = thumb ? thumbUrl(src, thumbW) : src;
    return <img src={shown} alt="" loading="lazy" decoding="async" className={className} style={style}
        onClick={onClick} title={title} onError={() => setBroken(true)} />;
}

// 列表卡片主图: 首图没上传成功时顺延到下一张可用图 (列表接口本就带了前几张),
// 全部加载失败才显示占位. onClick 回传当前真实展示的地址, 供放大查看.
export function ProductMainImg({ srcs, className, style, onClick }: {
    srcs: string[]; className?: string; style?: React.CSSProperties;
    onClick?: (e: React.MouseEvent<HTMLElement>, src: string) => void;
}) {
    const key = srcs.join('|');
    const [idx, setIdx] = React.useState(0);
    React.useEffect(() => { setIdx(0); }, [key]);
    const src = srcs[idx];
    if (!src) {
        return <BrokenImg className={className} style={style} />;
    }
    return (
        <img src={thumbUrl(src)} alt="" loading="lazy" decoding="async" className={className} style={style}
            onClick={onClick && ((e) => onClick(e, src))} onError={() => setIdx((i) => i + 1)} />
    );
}

export const fmtWeight = (g: number | null | undefined) => (g == null ? '—' : g >= 1000 ? `${(g / 1000).toFixed(2)} kg` : `${g} g`);
export const fmtDims = (l: number | null, w: number | null, h: number | null) => (l == null && w == null && h == null ? '—' : `${l ?? '?'} × ${w ?? '?'} × ${h ?? '?'} mm`);
export const fmtDate = (s: string | null | undefined) => (s ? s.slice(0, 10) : '—');
export const fmtDateTime = (s: string | null | undefined) => (s ? s.replace('T', ' ').slice(0, 16) : '—');
export const amazonURL = (asin: string) => `https://www.amazon.com/dp/${asin}`;
// 类目路径去掉前两级只留末端, 表格里省空间; 悬停看全路径.
export const categoryTail = (p: string | null | undefined) => (p ? p.split('>').map((s) => s.trim()).slice(-1)[0] : '—');
